import { useState, useEffect } from 'react';
import { X, CheckCircle, Database, Upload, BookOpen, Clock, AlertTriangle } from 'lucide-react';
import { supabase, getUserId } from '../lib/supabase';

interface WelcomeTutorialProps {
  onComplete: () => void;
}

export function WelcomeTutorial({ onComplete }: WelcomeTutorialProps) {
  const [loading, setLoading] = useState(false);
  const [testDataCreated, setTestDataCreated] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthenticated(!!user);
      if (!user) {
        setErrorMessage('Vous devez être connecté pour créer des données de test');
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      setIsAuthenticated(false);
    }
  }

  async function createTestData() {
    setLoading(true);
    setErrorMessage('');
    try {
      const userId = await getUserId();

      if (!userId) {
        throw new Error('Impossible de récupérer l\'identifiant utilisateur');
      }

      console.log('Creating test data for user:', userId);

      const categoriesData = [
        { name: 'Subventions', type: 'income' as const },
        { name: 'Ventes', type: 'income' as const },
        { name: 'Dons', type: 'income' as const },
        { name: 'Salaires', type: 'expense' as const },
        { name: 'Fournitures', type: 'expense' as const },
        { name: 'Loyer', type: 'expense' as const },
        { name: 'Charges', type: 'expense' as const },
      ];

      const categoriesToInsert = categoriesData.map(c => ({
        ...c,
        user_id: userId,
      }));

      console.log('Inserting categories...');
      const { data: categories, error: catError } = await supabase
        .from('categories')
        .insert(categoriesToInsert)
        .select();

      if (catError) {
        console.error('Error inserting categories:', catError);
        throw new Error(`Erreur lors de la création des catégories: ${catError.message}`);
      }

      if (!categories || categories.length === 0) {
        throw new Error('Aucune catégorie n\'a été créée');
      }

      console.log('Categories created:', categories.length);

      const catMap: Record<string, string> = {};
      categories?.forEach((cat) => {
        catMap[cat.name] = cat.id;
      });

      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
      const lastMonthYear = currentMonth === 1 ? currentYear - 1 : currentYear;

      const twoMonthsAgo = currentMonth <= 2 ? 12 - (2 - currentMonth) : currentMonth - 2;
      const twoMonthsAgoYear = currentMonth <= 2 ? currentYear - 1 : currentYear;

      const testTransactions = [
        { label: 'Subvention régionale', amount: 50000, date: `${twoMonthsAgoYear}-${String(twoMonthsAgo).padStart(2, '0')}-01`, category_id: catMap['Subventions'] },
        { label: 'Vente de production', amount: 3500, date: `${twoMonthsAgoYear}-${String(twoMonthsAgo).padStart(2, '0')}-15`, category_id: catMap['Ventes'] },
        { label: 'Don entreprise partenaire', amount: 2000, date: `${twoMonthsAgoYear}-${String(twoMonthsAgo).padStart(2, '0')}-20`, category_id: catMap['Dons'] },
        { label: 'Salaires équipe pédagogique', amount: -25000, date: `${twoMonthsAgoYear}-${String(twoMonthsAgo).padStart(2, '0')}-28`, category_id: catMap['Salaires'] },
        { label: 'Fournitures atelier', amount: -1200, date: `${lastMonthYear}-${String(lastMonth).padStart(2, '0')}-05`, category_id: catMap['Fournitures'] },
        { label: 'Loyer locaux', amount: -3500, date: `${lastMonthYear}-${String(lastMonth).padStart(2, '0')}-01`, category_id: catMap['Loyer'] },
        { label: 'Électricité et eau', amount: -450, date: `${lastMonthYear}-${String(lastMonth).padStart(2, '0')}-10`, category_id: catMap['Charges'] },
        { label: 'Vente de production', amount: 4200, date: `${lastMonthYear}-${String(lastMonth).padStart(2, '0')}-20`, category_id: catMap['Ventes'] },
        { label: 'Subvention département', amount: 25000, date: `${lastMonthYear}-${String(lastMonth).padStart(2, '0')}-25`, category_id: catMap['Subventions'] },
        { label: 'Salaires équipe pédagogique', amount: -25000, date: `${lastMonthYear}-${String(lastMonth).padStart(2, '0')}-28`, category_id: catMap['Salaires'] },
        { label: 'Loyer locaux', amount: -3500, date: `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`, category_id: catMap['Loyer'] },
        { label: 'Fournitures bureau', amount: -850, date: `${currentYear}-${String(currentMonth).padStart(2, '0')}-05`, category_id: catMap['Fournitures'] },
        { label: 'Charges énergie', amount: -520, date: `${currentYear}-${String(currentMonth).padStart(2, '0')}-10`, category_id: catMap['Charges'] },
        { label: 'Vente de production', amount: 5100, date: `${currentYear}-${String(currentMonth).padStart(2, '0')}-15`, category_id: catMap['Ventes'] },
      ];

      const transactionsToInsert = testTransactions.map(t => ({
        ...t,
        user_id: userId,
      }));

      console.log('Inserting transactions...');
      const { error: txError } = await supabase.from('transactions').insert(transactionsToInsert);
      if (txError) {
        console.error('Error inserting transactions:', txError);
        throw new Error(`Erreur lors de la création des transactions: ${txError.message}`);
      }

      console.log('Transactions created successfully');

      const testBudgetLines = [
        { category_id: catMap['Subventions'], year: currentYear, planned_amount: 150000 },
        { category_id: catMap['Ventes'], year: currentYear, planned_amount: 45000 },
        { category_id: catMap['Dons'], year: currentYear, planned_amount: 15000 },
        { category_id: catMap['Salaires'], year: currentYear, planned_amount: -180000 },
        { category_id: catMap['Fournitures'], year: currentYear, planned_amount: -25000 },
        { category_id: catMap['Loyer'], year: currentYear, planned_amount: -42000 },
        { category_id: catMap['Charges'], year: currentYear, planned_amount: -15000 },
      ];

      const budgetLinesToInsert = testBudgetLines.map(b => ({
        ...b,
        user_id: userId,
      }));

      console.log('Inserting budget lines...');
      const { error: budgetError } = await supabase.from('budget_lines').insert(budgetLinesToInsert);
      if (budgetError) {
        console.error('Error inserting budget lines:', budgetError);
        throw new Error(`Erreur lors de la création des lignes budgétaires: ${budgetError.message}`);
      }

      console.log('Budget lines created successfully');
      console.log('All test data created successfully');

      setTestDataCreated(true);
    } catch (error: any) {
      console.error('Error creating test data:', error);
      const message = error?.message || 'Une erreur inconnue est survenue';
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  }

  async function skipTutorial() {
    try {
      const userId = await getUserId();
      await supabase
        .from('user_profiles')
        .update({ tutorial_completed: true })
        .eq('id', userId);
      onComplete();
    } catch (error) {
      console.error('Error skipping tutorial:', error);
    }
  }

  async function completeTutorial() {
    try {
      const userId = await getUserId();
      await supabase
        .from('user_profiles')
        .update({ tutorial_completed: true })
        .eq('id', userId);
      onComplete();
    } catch (error) {
      console.error('Error completing tutorial:', error);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white relative">
          <button
            onClick={skipTutorial}
            className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <X size={24} />
          </button>
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white/20 p-3 rounded-lg">
              <BookOpen size={32} />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-center mb-2">
            Bienvenue dans votre outil de gestion !
          </h2>
          <p className="text-blue-100 text-center">
            Découvrez comment utiliser l'application avec des données de démonstration
          </p>
        </div>

        <div className="p-8 space-y-6">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="bg-green-600 p-3 rounded-lg flex-shrink-0">
                <Clock className="text-white" size={28} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Essai gratuit de 14 jours
                </h3>
                <p className="text-gray-700 mb-3">
                  Profitez de toutes les fonctionnalités de TrezoOne gratuitement pendant 14 jours, sans carte bancaire requise.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Accès complet à toutes les fonctionnalités</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Aucune carte bancaire nécessaire pendant l'essai</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Vous pourrez souscrire à un abonnement à l'issue de la période d'essai</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Database className="text-blue-600" size={24} />
              Option 1 : Tester avec des données de démonstration
            </h3>
            <p className="text-gray-700 mb-4">
              Nous vous proposons de créer des données de test pour découvrir toutes les fonctionnalités de l'application :
            </p>
            <ul className="space-y-2 mb-6 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span>Transactions d'exemple (recettes et dépenses)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span>Lignes budgétaires prévisionnelles</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span>Données réalistes pour une école de production</span>
              </li>
            </ul>

            {errorMessage && (
              <div className="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-red-800 text-sm font-semibold">Erreur lors de la création des données</p>
                    <p className="text-red-700 text-sm mt-1">{errorMessage}</p>
                    <button
                      onClick={() => {
                        setErrorMessage('');
                        createTestData();
                      }}
                      className="mt-3 text-sm text-red-700 font-medium hover:text-red-900 underline"
                    >
                      Réessayer
                    </button>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={createTestData}
              disabled={loading || testDataCreated || !isAuthenticated}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Création en cours...' : testDataCreated ? 'Données créées !' : 'Créer les données de test'}
            </button>
            {!isAuthenticated && !errorMessage && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                Vérification de l'authentification...
              </p>
            )}
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Upload className="text-gray-600" size={24} />
              Option 2 : Commencer avec vos propres données
            </h3>
            <p className="text-gray-700 mb-4">
              Vous pouvez directement importer vos données ou commencer à les saisir manuellement.
            </p>
            <button
              onClick={completeTutorial}
              className="w-full py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors"
            >
              Commencer sans données de test
            </button>
          </div>

          {testDataCreated && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <p className="text-green-800 mb-4 font-medium">
                Les données de test ont été créées avec succès ! Vous pouvez maintenant explorer l'application.
              </p>
              <p className="text-green-700 text-sm mb-4">
                Quand vous serez prêt à utiliser vos vraies données, rendez-vous dans l'onglet "Mon compte" pour supprimer les données de test.
              </p>
              <button
                onClick={completeTutorial}
                className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Commencer le tutoriel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
